// src/pages/Dashboard.js
// comment
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { auth, db } from "../firebase";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
  where,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

const EARLY_BIRD = {
  border: "rgba(234,179,8,0.35)",
  bg: "rgba(234,179,8,0.12)",
  text: "rgba(253,224,71,0.95)",
};

const REBUY = {
  border: "rgba(168,85,247,0.35)",
  bg: "rgba(168,85,247,0.12)",
  text: "rgba(216,180,254,0.95)",
};

const ADD_ON = {
  border: "rgba(59,130,246,0.35)",
  bg: "rgba(59,130,246,0.12)",
  text: "rgba(191,219,254,0.95)",
};

const DEALER_APPR = {
  border: "rgba(184,115,51,0.35)",
  bg: "rgba(184,115,51,0.14)",
  text: "rgba(245,208,168,0.95)",
};

const HIDDEN_FROM_MEMBERS = {
  border: "rgba(113,113,122,0.35)",
  bg: "rgba(113,113,122,0.12)",
  text: "rgba(228,228,231,0.95)",
};

function formatTimeFromDate(dateObj) {
  return dateObj.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function safeTournamentDateMillis(t) {
  try {
    if (t?.date?.toDate) return t.date.toDate().getTime();
    if (t?.date?.toMillis) return t.date.toMillis();
  } catch (e) {}
  return Number.MAX_SAFE_INTEGER;
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function getOpenSeatCount(table) {
  const seats = Array.isArray(table?.seats) ? table.seats : [];
  const maxSeats = Number(table?.maxSeats || 9);
  return Math.max(maxSeats - seats.length, 0);
}

function getEarlyBirdTimestamp(tournament) {
  if (typeof tournament?.earlyBirdDeadline?.toMillis === "function") {
    return tournament.earlyBirdDeadline;
  }
  if (typeof tournament?.earlyBirdCutoffAt?.toMillis === "function") {
    return tournament.earlyBirdCutoffAt;
  }
  return null;
}

function isEarlyBirdActive(tournament) {
  if (!tournament?.earlyBirdEnabled) return false;

  const cutoff = getEarlyBirdTimestamp(tournament);
  if (!cutoff?.toMillis) return false;

  return Date.now() <= cutoff.toMillis();
}

function isTournamentLiveForMembers(tournament) {
  return tournament?.communicationsLive === true;
}

function getDealerAppreciationConfig(tournament) {
  const enabled = !!tournament?.dealerAppreciationEnabled;
  const cost = Math.max(0, Number(tournament?.dealerAppreciationCost || 0));
  const bonusChips = Math.max(0, Number(tournament?.dealerAppreciationBonusChips || 0));
  const prizePoolAmount = Math.max(0, Number(tournament?.dealerAppreciationPrizePoolAmount || 0));
  const dealerAmount = Math.max(0, Number(tournament?.dealerAppreciationDealerAmount || 0));

  return {
    enabled: enabled && cost > 0,
    cost,
    bonusChips,
    prizePoolAmount,
    dealerAmount,
  };
}

function FeaturePill({ children, styleObj }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap"
      style={{
        borderColor: styleObj.border,
        background: styleObj.bg,
        color: styleObj.text,
      }}
    >
      {children}
    </span>
  );
}

function DealerAppreciationPrompt({
  prompt,
  processing,
  formatMoney,
  onClose,
  onChoose,
}) {
  if (!prompt) return null;

  const tournament = prompt.tournamentData || {};
  const dealerConfig = prompt.dealerConfig || {};
  const earlyBirdBonus = Math.max(0, Number(prompt.earlyBirdBonusChips || 0));
  const startingStack = Math.max(0, Number(tournament?.startingStack || 0));
  const buyIn = Math.max(0, Number(tournament?.buyIn || 0));

  const stackWithoutDealerAppr = startingStack + earlyBirdBonus;
  const stackWithDealerAppr =
    startingStack + earlyBirdBonus + Math.max(0, Number(dealerConfig.bonusChips || 0));

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl overflow-hidden">
        <div className="border-b border-zinc-800 px-5 py-4">
          <h3 className="text-xl font-bold text-[#B87333]">Dealer Appreciation</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Add optional dealer appreciation for <span className="text-zinc-100 font-semibold">{tournament?.name}</span>?
          </p>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Optional Cost</div>
              <div className="text-zinc-100 font-semibold mt-1">
                {formatMoney(dealerConfig.cost)}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Bonus Chips</div>
              <div className="text-zinc-100 font-semibold mt-1">
                +{Number(dealerConfig.bonusChips || 0).toLocaleString()}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Prize Pool</div>
              <div className="text-zinc-100 font-semibold mt-1">
                {formatMoney(dealerConfig.prizePoolAmount)}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Dealers</div>
              <div className="text-zinc-100 font-semibold mt-1">
                {formatMoney(dealerConfig.dealerAmount)}
              </div>
            </div>
          </div>

          {prompt.qualifiesForEarlyBird ? (
            <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-amber-200">
              Early Bird is active for this registration:{" "}
              <span className="font-semibold">
                +{earlyBirdBonus.toLocaleString()} chips
              </span>
            </div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Skip Dealer Appr</div>
              <div className="text-zinc-100 font-semibold mt-1">
                {stackWithoutDealerAppr.toLocaleString()} chips
              </div>
              <div className="text-zinc-400 text-sm mt-1">
                Total: {formatMoney(buyIn)}
              </div>
            </div>

            <div className="rounded-lg border border-[#B87333]/30 bg-[#B87333]/10 p-3">
              <div className="text-xs uppercase tracking-wide text-[#d8b08f]">Add Dealer Appr</div>
              <div className="text-zinc-100 font-semibold mt-1">
                {stackWithDealerAppr.toLocaleString()} chips
              </div>
              <div className="text-zinc-300 text-sm mt-1">
                Total: {formatMoney(buyIn + Math.max(0, Number(dealerConfig.cost || 0)))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-800 px-5 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="px-4 py-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={() => onChoose(false)}
            disabled={processing}
            className="px-4 py-3 rounded bg-zinc-700 hover:bg-zinc-600 text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {processing ? "Registering..." : "Skip"}
          </button>

          <button
            type="button"
            onClick={() => onChoose(true)}
            disabled={processing}
            className="px-4 py-3 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {processing ? "Registering..." : "Add Dealer Appr"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [tournaments, setTournaments] = useState([]);
  const [cashTables, setCashTables] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const [signups, setSignups] = useState({});
  const [registrationPrompt, setRegistrationPrompt] = useState(null);
  const [processingSignup, setProcessingSignup] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      setLoading(true);

      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const admin = userSnap.exists() && userSnap.data().role === "admin";
        setIsAdmin(admin);

        const tournamentsRef = collection(db, "tournaments");

        let tData = [];
        try {
          const tQuery = query(
            tournamentsRef,
            where("status", "in", ["registering", "running"]),
            orderBy("date", "asc")
          );

          const tSnap = await getDocs(tQuery);
          tData = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (err) {
          console.warn(
            "Preferred tournament query failed (likely needs composite index). Falling back.",
            err
          );

          const fallbackQuery = query(tournamentsRef, orderBy("date", "asc"));
          const tSnap = await getDocs(fallbackQuery);
          const all = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          tData = all.filter((t) => ["registering", "running"].includes(t.status));
        }

        tData.sort((a, b) => safeTournamentDateMillis(a) - safeTournamentDateMillis(b));

        if (!admin) {
          tData = tData.filter((t) => isTournamentLiveForMembers(t));
        }

        setTournaments(tData);

        const signupMap = {};
        for (const t of tData) {
          const entrantRef = doc(db, "tournaments", t.id, "entrants", currentUser.uid);
          const entrantSnap = await getDoc(entrantRef);
          signupMap[t.id] = entrantSnap.exists();
        }
        setSignups(signupMap);

        let cashGameRows = [];
        try {
          const cashGamesRef = collection(db, "cashTables");
          const cashGamesQ = query(cashGamesRef, orderBy("updatedAt", "desc"));
          const cashGamesSnap = await getDocs(cashGamesQ);

          cashGameRows = cashGamesSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((table) => {
              const isOpen = String(table?.status || "").toLowerCase() === "open";
              const openSeats = getOpenSeatCount(table);
              return isOpen && openSeats > 0;
            });
        } catch (err) {
          console.error("Error fetching cash games:", err);
        }

        setCashTables(cashGameRows);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const createEntrantRegistration = async ({
    tournamentId,
    tournamentData,
    userData,
    dealerAppreciationOptIn = false,
  }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const profileName = userData?.name || currentUser.displayName || currentUser.email;
    const profilePhotoUrl = String(userData?.profilePhotoUrl || "").trim();

    if (tournamentData.status !== "registering") {
      return alert("Registration is closed for this tournament.");
    }

    if (!isAdmin && !isTournamentLiveForMembers(tournamentData)) {
      return alert("This tournament is not live for members yet.");
    }

    const earlyBirdTs = getEarlyBirdTimestamp(tournamentData);
    const cutoffMillis =
      typeof earlyBirdTs?.toMillis === "function" ? earlyBirdTs.toMillis() : null;

    const qualifiesForEarlyBird =
      !!tournamentData?.earlyBirdEnabled &&
      Number(tournamentData?.earlyBirdBonusChips || 0) > 0 &&
      cutoffMillis != null &&
      Date.now() <= cutoffMillis;

    const earlyBirdBonusChips = qualifiesForEarlyBird
      ? Math.max(0, Number(tournamentData?.earlyBirdBonusChips || 0))
      : 0;

    const dealerConfig = getDealerAppreciationConfig(tournamentData);
    const dealerAppEnabled = dealerConfig.enabled;
    const dealerOptIn = dealerAppEnabled && !!dealerAppreciationOptIn;

    const entrantRef = doc(db, "tournaments", tournamentId, "entrants", currentUser.uid);

    await setDoc(entrantRef, {
      uid: currentUser.uid,
      name: profileName,
      displayName: profileName,
      email: currentUser.email,
      profilePhotoUrl,

      eliminated: false,
      addOnTaken: false,
      rebuys: 0,
      finishPlace: 0,

      earlyBirdQualified: qualifiesForEarlyBird,
      earlyBirdBonusChips,

      dealerAppreciationOptIn: dealerOptIn,
      dealerAppreciationCost: dealerOptIn ? dealerConfig.cost : 0,
      dealerAppreciationBonusChips: dealerOptIn ? dealerConfig.bonusChips : 0,
      dealerAppreciationPrizePoolAmount: dealerOptIn ? dealerConfig.prizePoolAmount : 0,
      dealerAppreciationDealerAmount: dealerOptIn ? dealerConfig.dealerAmount : 0,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    setSignups((prev) => ({ ...prev, [tournamentId]: true }));
    setTournaments((prev) =>
      prev.map((t) =>
        t.id === tournamentId
          ? { ...t, entrantsCount: (t.entrantsCount || 0) + 1 }
          : t
      )
    );
  };

  const signupForTournament = async (tournamentId) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const userSnap = await getDoc(doc(db, "users", currentUser.uid));
      const userData = userSnap.exists() ? userSnap.data() : {};

      const tournamentSnap = await getDoc(doc(db, "tournaments", tournamentId));
      if (!tournamentSnap.exists()) {
        return alert("Tournament not found.");
      }

      const tournamentData = tournamentSnap.data() || {};
      if (tournamentData.status !== "registering") {
        return alert("Registration is closed for this tournament.");
      }

      if (!isAdmin && !isTournamentLiveForMembers(tournamentData)) {
        return alert("This tournament is not live for members yet.");
      }

      const earlyBirdTs = getEarlyBirdTimestamp(tournamentData);
      const cutoffMillis =
        typeof earlyBirdTs?.toMillis === "function" ? earlyBirdTs.toMillis() : null;

      const qualifiesForEarlyBird =
        !!tournamentData?.earlyBirdEnabled &&
        Number(tournamentData?.earlyBirdBonusChips || 0) > 0 &&
        cutoffMillis != null &&
        Date.now() <= cutoffMillis;

      const earlyBirdBonusChips = qualifiesForEarlyBird
        ? Math.max(0, Number(tournamentData?.earlyBirdBonusChips || 0))
        : 0;

      const dealerConfig = getDealerAppreciationConfig(tournamentData);

      if (dealerConfig.enabled) {
        setRegistrationPrompt({
          tournamentId,
          tournamentData,
          userData,
          qualifiesForEarlyBird,
          earlyBirdBonusChips,
          dealerConfig,
        });
        return;
      }

      await createEntrantRegistration({
        tournamentId,
        tournamentData,
        userData,
        dealerAppreciationOptIn: false,
      });
    } catch (err) {
      console.error("Signup failed:", err);
      alert("Signup failed. Please try again.");
    }
  };

  const confirmSignupFromPrompt = async (dealerAppreciationOptIn) => {
    if (!registrationPrompt) return;

    setProcessingSignup(true);
    try {
      await createEntrantRegistration({
        tournamentId: registrationPrompt.tournamentId,
        tournamentData: registrationPrompt.tournamentData,
        userData: registrationPrompt.userData,
        dealerAppreciationOptIn,
      });
      setRegistrationPrompt(null);
    } catch (err) {
      console.error("Signup from dealer appreciation prompt failed:", err);
      alert("Signup failed. Please try again.");
    }
    setProcessingSignup(false);
  };

  const cancelSignup = async (tournamentId) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const t = tournaments.find((x) => x.id === tournamentId);
      if (!t || t.status !== "registering") {
        return alert("Registration is locked for this tournament.");
      }

      const entrantRef = doc(db, "tournaments", tournamentId, "entrants", currentUser.uid);
      await deleteDoc(entrantRef);

      setSignups((prev) => ({ ...prev, [tournamentId]: false }));
      setTournaments((prev) =>
        prev.map((t) =>
          t.id === tournamentId
            ? { ...t, entrantsCount: Math.max((t.entrantsCount || 0) - 1, 0) }
            : t
        )
      );
    } catch (err) {
      console.error("Cancel failed:", err);
      alert("Cancel failed. Please try again.");
    }
  };

  const openTitle = useMemo(
    () => (isAdmin ? "Open / Draft Tournaments" : "Open Tournaments"),
    [isAdmin]
  );

  return (
    <Layout>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-[#B87333]">Dashboard</h2>
          <p className="text-zinc-400 mt-1">
            Upcoming signups, tournaments in progress, and open cash game seats.
          </p>
        </div>

        {isAdmin ? (
          <Link
            to="/create-tournament"
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold"
          >
            + Create Tournament
          </Link>
        ) : null}
      </div>

      {loading ? <p className="text-zinc-300">Loading...</p> : null}

      <h3 className="text-2xl font-bold text-[#B87333] mb-4">{openTitle}</h3>

      {tournaments.length === 0 ? (
        <p className="text-zinc-400">No tournaments currently open.</p>
      ) : (
        <div className="space-y-4 mb-10">
          {tournaments.map((t) => {
            const isSignedUp = !!signups[t.id];
            const count = t.entrantsCount || 0;

            let dateLabel = "";
            let timeLabel = "";

            if (t.date?.toDate) {
              const d = t.date.toDate();
              dateLabel = d.toDateString();
              timeLabel = formatTimeFromDate(d);
            } else {
              dateLabel = t.date || "";
              timeLabel = t.startTime || "";
            }

            const status = t.status || "—";
            const isRunning = status === "running";
            const earlyBirdActive = isEarlyBirdActive(t);
            const rebuysActive = !!t.rebuysEnabled;
            const addOnsActive = !!t.addOnsEnabled;
            const dealerAppreciation = getDealerAppreciationConfig(t);
            const buyIn = Number(t?.buyIn || 0);
            const location = String(t?.location || "").trim();
            const isLiveForMembers = isTournamentLiveForMembers(t);

            return (
              <div
                key={t.id}
                className="bg-zinc-900 p-4 rounded-lg border border-zinc-800"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xl font-semibold text-[#B87333]">
                      <Link to={`/tournament/${t.id}`} className="hover:underline">
                        {t.name}
                      </Link>
                    </h4>

                    <p className="text-zinc-200 mt-1">
                      {dateLabel}
                      {timeLabel ? ` — ${timeLabel}` : ""}
                    </p>

                    {location ? (
                      <p className="text-zinc-400 mt-1">
                        Location:{" "}
                        <span className="font-semibold text-zinc-200">{location}</span>
                      </p>
                    ) : null}

                    <p className="text-zinc-400 mt-1">
                      Buy-In:{" "}
                      <span className="font-semibold text-zinc-200">
                        {formatMoney(buyIn)}
                      </span>
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {earlyBirdActive ? (
                        <FeaturePill styleObj={EARLY_BIRD}>★ Early Bird</FeaturePill>
                      ) : null}

                      {rebuysActive ? (
                        <FeaturePill styleObj={REBUY}>↻ Rebuy</FeaturePill>
                      ) : null}

                      {addOnsActive ? (
                        <FeaturePill styleObj={ADD_ON}>+ Add-On</FeaturePill>
                      ) : null}

                      {dealerAppreciation.enabled ? (
                        <FeaturePill styleObj={DEALER_APPR}>♥ Dealer Appr</FeaturePill>
                      ) : null}

                      {isAdmin && !isLiveForMembers ? (
                        <FeaturePill styleObj={HIDDEN_FROM_MEMBERS}>
                          Hidden from Members
                        </FeaturePill>
                      ) : null}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-3 text-sm">
                      <span className="text-zinc-300">
                        Status:{" "}
                        <span
                          className={`font-semibold ${
                            isRunning ? "text-green-400" : "text-zinc-100"
                          }`}
                        >
                          {status}
                        </span>
                      </span>

                      <span className="text-zinc-300">
                        Registered:{" "}
                        <span className="font-semibold text-zinc-100">{count}</span>
                      </span>
                    </div>
                  </div>

                  <div className="mt-2 sm:mt-0 flex flex-col gap-2 sm:items-end">
                    {isSignedUp ? (
                      <div className="flex items-center gap-3 flex-wrap justify-end">
                        <span className="inline-block px-3 py-2 rounded bg-zinc-800 text-zinc-200">
                          ✅ You’re registered
                        </span>

                        {t.status === "registering" ? (
                          <button
                            onClick={() => cancelSignup(t.id)}
                            className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                          >
                            Cancel Registration
                          </button>
                        ) : (
                          <span className="text-zinc-400 text-sm">
                            Registration locked (tournament {t.status})
                          </span>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => signupForTournament(t.id)}
                        disabled={t.status !== "registering"}
                        className={`px-4 py-2 rounded font-semibold ${
                          t.status === "registering"
                            ? "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                            : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                        }`}
                      >
                        {t.status === "registering" ? "Sign Up" : "Registration Closed"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h3 className="text-2xl font-bold text-[#B87333] mb-4">Open Cash Game Tables</h3>

      {cashTables.length === 0 ? (
        <p className="text-zinc-400">No active cash game tables with open seats right now.</p>
      ) : (
        <div className="space-y-4">
          {cashTables.map((table) => {
            const tableName = table?.name || table?.tableName || "Cash Game Table";
            const stakes =
              table?.stakesLabel ||
              (table?.smallBlind && table?.bigBlind
                ? `${formatMoney(table.smallBlind)}/${formatMoney(table.bigBlind)}`
                : "—");

            const maxSeats = Number(table?.maxSeats || 0);
            const seats = Array.isArray(table?.seats) ? table.seats : [];
            const occupiedSeats = seats.filter((s) => !!s).length;
            const openSeats = getOpenSeatCount(table);

            return (
              <div
                key={table.id}
                className="bg-zinc-900 p-4 rounded-lg border border-zinc-800"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h4 className="text-xl font-semibold text-[#B87333]">
                      <Link to="/cash-games" className="hover:underline">
                        {tableName}
                      </Link>
                    </h4>

                    <p className="text-zinc-200 mt-1">{stakes}</p>

                    <div className="mt-2 flex flex-wrap gap-3 text-sm">
                      <span className="text-zinc-300">
                        Status: <span className="font-semibold text-green-400">open</span>
                      </span>

                      <span className="text-zinc-300">
                        Seated:{" "}
                        <span className="font-semibold text-zinc-100">
                          {occupiedSeats}
                          {maxSeats > 0 ? ` / ${maxSeats}` : ""}
                        </span>
                      </span>

                      <span className="text-zinc-300">
                        Open Seats:{" "}
                        <span className="font-semibold text-zinc-100">{openSeats}</span>
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 sm:mt-0">
                    <Link
                      to="/cash-games"
                      className="inline-flex items-center justify-center px-4 py-2 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold"
                    >
                      View Table
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DealerAppreciationPrompt
        prompt={registrationPrompt}
        processing={processingSignup}
        formatMoney={formatMoney}
        onClose={() => {
          if (!processingSignup) setRegistrationPrompt(null);
        }}
        onChoose={confirmSignupFromPrompt}
      />
    </Layout>
  );
}
